import { Inject, Service } from 'typedi';
import { action, computed, makeObservable, observable } from 'mobx';
import { UserService, BaseUserToken, AnonymousUserToken, EnterpriseUserToken } from '@enterprise/common';
import { environment } from '../environment/environment';
import EnterpriseApiHttp from '../services/http/EnterpriseApiHttp';
import { Notifications } from '../pages/Notifications';
import RoleConstants from '../constants/RoleConstants';
import type { Auth0ContextInterface } from '@auth0/auth0-react';
import { RouterPaths } from '../router/RouterPaths';

@Service()
export default class AppStore {
    @observable token: BaseUserToken = new AnonymousUserToken();
    @observable private _authIsLoading = true;
    @observable private _authIsAuthenticated = false;
    authorizing = false;

    private _auth0Client?: Auth0ContextInterface;

    constructor(
        private readonly userService: UserService,
        @Inject('http') private readonly http: EnterpriseApiHttp,
        @Inject('httpOnboarding') private readonly httpOnboarding: EnterpriseApiHttp,
    ) {
        makeObservable(this);
        this.http.setAppSessionMethod(this.checkAppSession);
        this.httpOnboarding.setAppSessionMethod(this.checkAppSession);
    }

    @computed get isAuthReady(): boolean {
        return !this._authIsLoading;
    }

    @computed get isAuthenticated(): boolean {
        return this._authIsAuthenticated && this.token.isAuthenticated();
    }

    @action setAuthClient(authClient: Auth0ContextInterface) {
        this._auth0Client = authClient;
        this._authIsLoading = authClient?.isLoading ?? true;
        this._authIsAuthenticated = Boolean(authClient?.isAuthenticated);
    }

    getAccessToken = async (): Promise<string | undefined> => {
        const { AUTH0 } = environment;
        try {
            return await this._auth0Client?.getAccessTokenSilently({
                authorizationParams: {
                    audience: AUTH0.audience,
                    scope: AUTH0.scope,
                },
            });
        } catch (error) {
            console.error('Auth0 getAccessToken error', error);
            return undefined;
        }
    };

    @action checkAppSession = async () => {
        return await this.getAccessToken();
    };

    @action redirectIfNotAdmin = (): void => {
        if (this.token?.hasRole(RoleConstants.SUPER_ADMIN) || this.token?.hasRole(RoleConstants.ONBOARDING_ADMIN)) {
            return;
        }
        void this.logout();
    };

    @action checkIsActive = (token): boolean => {
        return token.user.isActive;
    };

    @action errorNotActive = (): void => {
        void this.logout();
    };

    @action async authorize(idToken: string) {
        if (this.authorizing) {
            return;
        }
        this.authorizing = true;

        return this.userService
            .authenticate(idToken)
            .then((token) => {
                if (token instanceof Error) {
                    throw token;
                }
                this.authorizing = false;

                if (!token || token === 'USER_NOT_FOUND') {
                    return token;
                }
                this.token = token;
                (this.token as EnterpriseUserToken).access_token = idToken;
                this.redirectIfNotAdmin();
                if (token.user && !this.checkIsActive(token)) {
                    this.errorNotActive();
                    return;
                }
                return token;
            })
            .catch((err) => {
                this.authorizing = false;
                Notifications.error(err.message);
                return err;
            });
    }

    refreshAuthorization = async (): Promise<BaseUserToken | undefined> => {
        const accessToken = await this.getAccessToken();
        if (!accessToken) {
            return undefined;
        }

        return this.authorize(accessToken) as Promise<BaseUserToken | undefined>;
    };

    toggleAuthStatus = (status: boolean = !this.authorizing) => (this.authorizing = status);

    @action logout = async (): Promise<any> => {
        try {
            this.token = await this.userService.logout(this.token);
            const redirectUrl = `${window.location.origin}${RouterPaths.BasePages.Logout}`;
            this._auth0Client?.logout({ logoutParams: { returnTo: redirectUrl } });
        } catch (err) {
            console.error(err);
        }
        return this.token;
    };

    @action async bootstrap(): Promise<any> {
        try {
            const token = await this.userService.restore();
            const accessToken = await this.getAccessToken();
            this.token = token;
            if (accessToken) {
                (this.token as EnterpriseUserToken).access_token = accessToken;
            }
            return this.token;
        } catch (token) {
            this.token = token as BaseUserToken;
            return this.token;
        }
    }
}
